import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  Trash2,
  Loader2,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  Presentation,
  FileArchive,
  File as FileIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import {
  listEventAttachments,
  uploadEventAttachment,
  deleteEventAttachment,
  formatBytes,
  fileCategoryFor,
  ATTACHMENT_ACCEPT_ATTR,
  MAX_EVENT_BYTES,
  type EventAttachment,
} from "@/lib/event-attachments";
import { panelPill, panelRow } from "@/lib/panel-ui";
import { cn } from "@/lib/utils";

function CategoryIcon({ mime }: { mime: string }) {
  const cat = fileCategoryFor(mime);
  const cls = "h-5 w-5 shrink-0 text-muted-foreground";
  switch (cat) {
    case "pdf":
      return <FileText className={cls} />;
    case "image":
      return <ImageIcon className={cls} />;
    case "sheet":
      return <FileSpreadsheet className={cls} />;
    case "slides":
      return <Presentation className={cls} />;
    case "zip":
      return <FileArchive className={cls} />;
    case "doc":
      return <FileText className={cls} />;
    default:
      return <FileIcon className={cls} />;
  }
}

export function EventAttachmentsManager({ eventId }: { eventId: string }) {
  const { t } = useTranslation('producerEventForm');
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const queryKey = ["event-attachments", eventId];

  const { data: attachments = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => listEventAttachments(eventId),
  });

  const used = attachments.reduce((s, a) => s + (a.file_size_bytes || 0), 0);
  const usagePercent = Math.min(100, (used / MAX_EVENT_BYTES) * 100);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadEventAttachment({ eventId, file }),
    onSuccess: () => {
      toast({ title: t('attachments.toast.uploadSuccess') });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e) =>
      toast({
        title: t('attachments.toast.uploadErrorTitle'),
        description: e?.message || t('attachments.toast.uploadErrorDefault'),
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteEventAttachment(id),
    onSuccess: () => {
      toast({ title: t('attachments.toast.deleteSuccess') });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e) =>
      toast({
        title: t('attachments.toast.deleteErrorTitle'),
        description: e?.message || t('attachments.toast.deleteErrorDefault'),
        variant: "destructive",
      }),
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Sequencial: cada upload depende do total atualizado do evento.
      for (const file of Array.from(files)) {
        await uploadMutation.mutateAsync(file);
      }
    } catch {
      // erro já tratado no onError de cada mutação
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-foreground">{t('attachments.header.title')}</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT_ATTR}
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t('attachments.usage.summary', { used: formatBytes(used), total: formatBytes(MAX_EVENT_BYTES) })}
          </span>
          <span>{Math.round(usagePercent)}%</span>
        </div>
        <Progress value={usagePercent} className="h-2" />
      </div>

      <Button
        type="button"
        variant="outline"
        disabled={uploading || used >= MAX_EVENT_BYTES}
        onClick={() => inputRef.current?.click()}
        className={cn("w-full sm:w-auto", panelPill)}
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('attachments.button.uploading')}
          </>
        ) : (
          <>
            <Upload className="h-4 w-4 mr-2" /> {t('attachments.button.add')}
          </>
        )}
      </Button>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('attachments.list.loading')}
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('attachments.list.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a: EventAttachment) => (
            <li
              key={a.id}
              className={cn("flex items-center gap-3 px-4 py-3", panelRow)}
            >
              <CategoryIcon mime={a.mime_type} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground" title={a.file_name}>
                  {a.file_name}
                </p>
                <p className="text-xs text-muted-foreground">{formatBytes(a.file_size_bytes)}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(a.id)}
                aria-label={t('attachments.list.removeAria', { name: a.file_name })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
